import { useCallback, useEffect, useState } from "react";
import { t } from "../locale";
import { slottingErrorText } from "../lib/slotting-error";
import {
  fetchWaitlistPage,
  cancelWaitlist,
  type WaitlistPageData,
} from "../../lib/slottingApi";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  WAITING: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
  PROMOTED: { bg: "rgba(34,197,94,0.15)", color: "#16a34a" },
  EXPIRED: { bg: "rgba(100,116,139,0.15)", color: "#475569" },
  CANCELLED: { bg: "rgba(239,68,68,0.15)", color: "#dc2626" },
};

const STATUS_LABELS: Record<string, Parameters<typeof t>[0]> = {
  WAITING: "slottingWaitlistStatusWaiting",
  PROMOTED: "slottingWaitlistStatusPromoted",
  EXPIRED: "slottingWaitlistStatusExpired",
  CANCELLED: "slottingWaitlistStatusCancelled",
};

export function SlottingWaitlist({ waitlistEntryId: propId, theme }: { waitlistEntryId?: string; theme?: 'light' | 'dark' }) {
  const params = new URLSearchParams(window.location.search);
  const token = propId || params.get("waitlistEntryId") || "";

  const [data, setData] = useState<WaitlistPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState("");
  const eventTz = useEventTimezone();

  useEffect(() => {
    let active = true;
    if (!token) { setLoading(false); setError(t("slottingWaitlistNotFound")); return; }
    fetchWaitlistPage(token)
      .then((d) => { if (!active) return; setData(d); })
      .catch((err) => { if (!active) return; setError(slottingErrorText(err, "slottingWaitlistNotFound")); })
      .finally(() => { if (!active) return; setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const handleLeave = useCallback(async () => {
    setLeaveLoading(true);
    setLeaveError("");
    try {
      await cancelWaitlist(token);
      setLeft(true);
    } catch (err) {
      setLeaveError(slottingErrorText(err, "slottingFailedToLeaveWaitlist"));
    } finally {
      setLeaveLoading(false);
    }
  }, [token]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
        <div style={{ width: 20, height: 20, border: "2px solid #ddd", borderTopColor: "#121212", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error || !data) {
    return <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>{error || t("slottingWaitlistNotFound")}</div>;
  }

  const { waitlistEntry, appointment, offering } = data;
  const status = waitlistEntry.status;
  const statusStyle = STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#475569" };
  const offeringTz = offering?.timezone || eventTz;

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 24 }}>{t("slottingWaitlistStatus")}</h2>

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: statusStyle.bg, color: statusStyle.color, alignSelf: "flex-start" }}>
          {STATUS_LABELS[status] ? t(STATUS_LABELS[status]) : status}
        </span>

        {appointment && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingYourAppointment")}</div>
            <div>{new Date(appointment.startDt).toLocaleDateString(undefined, { timeZone: offeringTz, weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
            <div>
              {new Date(appointment.startDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}
              {" \u2013 "}
              {new Date(appointment.endDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        )}

        {offering && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingOffering")}</div>
            <div>{offering.name}</div>
          </div>
        )}

        {status === "WAITING" && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingWaitlistPosition")}</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>#{waitlistEntry.position}</div>
          </div>
        )}
      </div>

      {/* Promoted */}
      {status === "PROMOTED" && appointment?.appointmentId && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "rgba(34,197,94,0.1)", color: "#16a34a", fontSize: 14 }}>
          <p style={{ fontWeight: 500, marginBottom: 8 }}>{t("slottingWaitlistPromoted")}</p>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.delete("waitlistEntryId");
              url.searchParams.set("appointmentId", appointment.appointmentId);
              window.history.replaceState({}, "", url.toString());
              window.location.reload();
            }}
            style={{ padding: "8px 16px", borderRadius: 8, background: "#16a34a", color: "#fff", border: "none", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
          >
            {t("slottingViewAppointment")}
          </button>
        </div>
      )}

      {/* Waiting — leave */}
      {status === "WAITING" && !left && (
        <div style={{ marginTop: 24 }}>
          {leaveError && (
            <div style={{ padding: 12, borderRadius: 8, background: "rgba(239,68,68,0.1)", color: "#dc2626", fontSize: 14, marginBottom: 12 }}>
              {leaveError}
            </div>
          )}
          <button
            onClick={handleLeave}
            disabled={leaveLoading}
            style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #fca5a5", color: "#dc2626", background: "transparent", fontSize: 14, fontWeight: 500, cursor: "pointer", opacity: leaveLoading ? 0.5 : 1 }}
          >
            {leaveLoading ? t("slottingLeaving") : t("slottingLeaveWaitlist")}
          </button>
        </div>
      )}

      {/* Left */}
      {left && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "#f8fafc", color: "#64748b", fontSize: 14 }}>
          {t("slottingLeftWaitlist")}
        </div>
      )}

      {/* Cancelled / Expired */}
      {(status === "CANCELLED" || status === "EXPIRED") && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "#f8fafc", color: "#64748b", fontSize: 14 }}>
          {t("slottingWaitlistInactive")}
        </div>
      )}
    </div>
  );
}
